import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DeliveryMap from '../components/DeliveryMap';
import { API_URL } from '../services/api';
import './OrderTracking.css';

// GVS Head Office - Zaria, Kaduna (Correct Location)
const GVS_HEAD_OFFICE = {
  lat: 11.0749,  // Zaria, Kaduna coordinates
  lng: 7.6963,
  name: 'Gimbiya Value Store - Head Office',
  address: 'Jamaa Road, Zaria 810106, Kaduna, Nigeria',
  fullAddress: '4MRF+X35, Jamaa Road, Zaria 810106, Kaduna'
};

// Function to get full image URL
const getFullImageUrl = (imagePath: string): string => {
  if (!imagePath) return '/placeholder.png';
  if (imagePath.startsWith('http')) return imagePath;
  const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
  return `${API_URL.replace('/api', '')}/${cleanPath}`;
};

// Function to geocode address to coordinates (using OpenStreetMap Nominatim)
const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
  try {
    const encodedAddress = encodeURIComponent(address + ", Nigeria");
    const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodedAddress}&format=json&limit=1`);
    const data = await response.json();
    
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon)
      };
    }
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

// Fallback coordinates based on Nigerian states
const getStateCoordinates = (state: string): { lat: number; lng: number } => {
  const stateCoords: { [key: string]: { lat: number; lng: number } } = {
    'Kaduna': { lat: 10.5264, lng: 7.4388 },
    'Abuja': { lat: 9.0765, lng: 7.3986 },
    'Abia': { lat: 5.5326, lng: 7.4862 },
    'Adamawa': { lat: 9.3269, lng: 12.3984 },
    'Akwa Ibom': { lat: 4.9057, lng: 7.8537 },
    'Anambra': { lat: 6.2209, lng: 7.0715 },
    'Bauchi': { lat: 10.3103, lng: 9.8448 },
    'Bayelsa': { lat: 4.7719, lng: 6.0699 },
    'Benue': { lat: 7.7337, lng: 8.7451 },
    'Borno': { lat: 11.8333, lng: 13.1500 },
    'Cross River': { lat: 5.9342, lng: 8.3379 },
    'Delta': { lat: 5.5000, lng: 6.0000 },
    'Ebonyi': { lat: 6.2500, lng: 8.0833 },
    'Edo': { lat: 6.3667, lng: 5.6167 },
    'Ekiti': { lat: 7.6667, lng: 5.3167 },
    'Enugu': { lat: 6.4527, lng: 7.5106 },
    'Gombe': { lat: 10.2833, lng: 11.1667 },
    'Imo': { lat: 5.5720, lng: 7.0588 },
    'Jigawa': { lat: 12.0000, lng: 9.7500 },
    'Kano': { lat: 12.0022, lng: 8.5919 },
    'Katsina': { lat: 12.0000, lng: 7.5000 },
    'Kebbi': { lat: 11.5000, lng: 4.0000 },
    'Kogi': { lat: 7.8000, lng: 6.7500 },
    'Kwara': { lat: 8.5000, lng: 4.5500 },
    'Lagos': { lat: 6.5244, lng: 3.3792 },
    'Nasarawa': { lat: 8.5000, lng: 8.0000 },
    'Niger': { lat: 9.5833, lng: 5.5833 },
    'Ogun': { lat: 7.0000, lng: 3.3500 },
    'Ondo': { lat: 7.2500, lng: 5.0000 },
    'Osun': { lat: 7.5000, lng: 4.5000 },
    'Oyo': { lat: 7.3775, lng: 3.9470 },
    'Plateau': { lat: 9.9120, lng: 8.8581 },
    'Rivers': { lat: 4.8156, lng: 7.0498 },
    'Sokoto': { lat: 13.0667, lng: 5.2333 },
    'Taraba': { lat: 7.5000, lng: 10.0000 },
    'Yobe': { lat: 12.0000, lng: 11.5000 },
    'Zamfara': { lat: 12.1667, lng: 6.6667 }
  };
  
  return stateCoords[state] || { lat: 11.0749, lng: 7.6963 };
};

const OrderTracking: React.FC = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deliveryCoords, setDeliveryCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [geocoding, setGeocoding] = useState(false);

  // Wrap fetchOrderDetails in useCallback
  const fetchOrderDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data.order);
      setError(null);
      
      // Geocode the delivery address
      if (response.data.order?.shippingAddress) {
        setGeocoding(true);
        const address = response.data.order.shippingAddress;
        const addressString = `${address.address}, ${address.city}, ${address.state}`;
        
        let coords = await geocodeAddress(addressString);
        
        // If geocoding fails, use state-based fallback
        if (!coords && address.state) {
          coords = getStateCoordinates(address.state);
          console.log(`Using fallback coordinates for ${address.state}:`, coords);
        }
        
        if (coords) {
          setDeliveryCoords(coords);
        }
        setGeocoding(false);
      }
    } catch (error: any) {
      console.error('Error fetching order:', error);
      setError(error.response?.data?.error || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  // Get pickup location - GVS Head Office in Zaria, Kaduna
  const getPickupLocation = () => {
    return GVS_HEAD_OFFICE;
  };

  // Get delivery location from order with proper coordinates
  const getDeliveryLocation = () => {
    if (deliveryCoords && order?.shippingAddress) {
      return {
        lat: deliveryCoords.lat,
        lng: deliveryCoords.lng,
        name: order.shippingAddress.fullName || 'Customer Location',
        address: `${order.shippingAddress.address}, ${order.shippingAddress.city}, ${order.shippingAddress.state}`
      };
    }
    
    // Fallback - try to get from state
    if (order?.shippingAddress?.state) {
      const fallbackCoords = getStateCoordinates(order.shippingAddress.state);
      return {
        lat: fallbackCoords.lat,
        lng: fallbackCoords.lng,
        name: order.shippingAddress.fullName || 'Customer Location',
        address: `${order.shippingAddress.address}, ${order.shippingAddress.city}, ${order.shippingAddress.state}`
      };
    }
    
    // Default fallback (Zaria location)
    return {
      lat: 11.0749,
      lng: 7.6963,
      name: 'Customer Location',
      address: 'Delivery Address'
    };
  };

  // Updated tracking steps - removed 'confirmed', added 'returned' and 'refunded'
  const trackingSteps = [
    { status: 'pending', label: 'Order Placed', icon: '📝', date: order?.createdAt },
    { status: 'paid', label: 'Payment Confirmed', icon: '✅', date: order?.paidAt },
    { status: 'shipped', label: 'Shipped', icon: '🚚', date: order?.shippedAt },
    { status: 'delivered', label: 'Delivered', icon: '📦', date: order?.deliveredAt },
    { status: 'returned', label: 'Returned', icon: '🔄', date: order?.returnedAt },
    { status: 'refunded', label: 'Refunded', icon: '💰', date: order?.refundedAt }
  ];
 
  const getStepStatus = (stepStatus: string) => {
    // Define the order status sequence
    const statusSequence = ['pending', 'paid', 'shipped', 'delivered', 'returned', 'refunded'];
    const currentIndex = statusSequence.indexOf(order?.status);
    const stepIndex = statusSequence.indexOf(stepStatus);
    
    // If order is cancelled, show all steps as cancelled except delivered
    if (order?.status === 'cancelled') {
      if (stepStatus === 'cancelled') return 'cancelled';
      return 'cancelled';
    }
    
    if (currentIndex > stepIndex) return 'completed';
    if (currentIndex === stepIndex) return 'active';
    return 'pending';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Pending';
    return new Date(dateString).toLocaleDateString('en-NG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusDisplay = () => {
    const statusMap: { [key: string]: { text: string; color: string; icon: string } } = {
      pending: { text: 'Pending Payment', color: '#ff9800', icon: '⏳' },
      paid: { text: 'Processing Order', color: '#9c27b0', icon: '⚙️' },
      shipped: { text: 'Order Shipped', color: '#00bcd4', icon: '🚚' },
      delivered: { text: 'Order Delivered', color: '#4caf50', icon: '✅' },
      cancelled: { text: 'Order Cancelled', color: '#f44336', icon: '❌' },
      returned: { text: 'Order Returned', color: '#ff5722', icon: '🔄' },
      refunded: { text: 'Order Refunded', color: '#795548', icon: '💰' }
    };
    return statusMap[order?.status] || { text: order?.status || 'Unknown', color: '#757575', icon: '❓' };
  };

  const getPaymentStatusDisplay = () => {
    switch (order?.paymentStatus) {
      case 'paid':
        return {
          text: '✓ Paid',
          color: '#4caf50',
          icon: '✅'
        };

      case 'partial':
      case 'deposit_paid':
        return {
          text: 'Deposit Paid',
          color: '#ff9800',
          icon: '💰'
        };

      case 'processing':
        return {
          text: 'Payment Processing',
          color: '#2196f3',
          icon: '⏳'
        };

      case 'failed':
        return {
          text: 'Payment Failed',
          color: '#f44336',
          icon: '❌'
        };

      case 'cancelled':
        return {
          text: 'Payment Cancelled',
          color: '#757575',
          icon: '🚫'
        };

      case 'refunded':
        return {
          text: 'Payment Refunded',
          color: '#795548',
          icon: '↩️'
        };

      case 'pending':
      default:
        return {
          text: 'Awaiting Payment',
          color: '#ff9800',
          icon: '⏳'
        };
    }
  };

  const orderStatus = getStatusDisplay();
  const paymentStatus = getPaymentStatusDisplay();

  if (loading || geocoding) {
    return (
      <div className="tracking-container">
        <div className="tracking-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">{geocoding ? 'Getting delivery location...' : 'Loading your order details...'}</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="tracking-container">
        <div className="tracking-error">
          <div className="error-icon">⚠️</div>
          <h2 className="error-title">Unable to Load Order</h2>
          <p className="error-message">{error || 'Order not found'}</p>
          <button onClick={() => navigate('/my-orders')} className="back-button">
            ← Back to My Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tracking-container">
      <div className="tracking-header">
        <h1>Track Your Order</h1>
        <div className="order-id">Order #{order.orderReference || order._id.slice(-8)}</div>
      </div>

      {/* Delivery Map with Correct Locations */}
      <DeliveryMap
        pickupLocation={getPickupLocation()}
        deliveryLocation={getDeliveryLocation()}
        orderStatus={order.status}
      />

      <div className="progress-timeline">
        <div className="timeline-title">
          <span>📊</span> Order Progress
        </div>
        <div className="timeline">
          {trackingSteps.map((step, idx) => {
            // Only show steps that are relevant based on order status
            if (order.status === 'cancelled' && step.status !== 'pending') {
              return null;
            }
            
            // For returned/refunded, show all steps
            const stepStatus = getStepStatus(step.status);
            return (
              <div key={idx} className={`timeline-step ${stepStatus}`}>
                <div className="step-marker">{step.icon}</div>
                <div className="step-content">
                  <div className="step-label">{step.label}</div>
                  {step.date && <div className="step-date">{formatDate(step.date)}</div>}
                </div>
              </div>
            );
          })}
          
          {/* Add cancelled step if order is cancelled */}
          {order.status === 'cancelled' && (
            <div className="timeline-step cancelled">
              <div className="step-marker">❌</div>
              <div className="step-content">
                <div className="step-label">Order Cancelled</div>
                <div className="step-date">{formatDate(order.cancelledAt || order.updatedAt)}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="order-info-card">
        <div className="info-section">
          <div className="section-title">📋 Order Summary</div>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label">Order Status</div>
              <div className="info-value" style={{ color: orderStatus.color, fontWeight: 'bold' }}>
                {orderStatus.icon} {orderStatus.text}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Payment Method</div>
              <div className="info-value">
                💳 Paystack
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Payment Status</div>
              <div className="info-value" style={{ color: paymentStatus.color, fontWeight: 'bold' }}>
                {paymentStatus.icon} {paymentStatus.text}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Order Date</div>
              <div className="info-value">{formatDate(order.createdAt)}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Total Amount</div>
              <div className="info-value total">₦{order.total.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="items-list">
        <div className="section-title">🛍️ Items Ordered</div>
        {order.items.map((item: any, idx: number) => (
          <div key={idx} className="order-item">
            <img 
              src={getFullImageUrl(item.image)} 
              alt={item.name} 
              className="item-image"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/placeholder.png';
              }}
            />
            <div className="item-details">
              <div className="item-name">{item.name}</div>
              <div className="item-price">₦{item.price.toLocaleString()}</div>
              <div className="item-quantity">Qty: {item.quantity}</div>
            </div>
            <div className="item-subtotal">₦{(item.price * item.quantity).toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="action-buttons">
        <button onClick={() => navigate('/my-orders')} className="action-button secondary">
          ← Back to Orders
        </button>
        <button onClick={() => navigate('/products')} className="action-button primary">
          Continue Shopping →
        </button>
      </div>
    </div> 
  );
};

export default OrderTracking;